/**
 * Navigation singleton — pure-JS schema + merge helper.
 *
 * EPIC-13 · FEAT-13.4 · STORY-13.4.01.
 *
 * Document path: `/site_settings/nav` (top-level singleton).
 *
 * Stored shape (after `mergeNav`):
 *   {
 *     header: NavItem[],
 *     // Footer is an array of column objects (NOT nested arrays —
 *     // Firestore rejects nested arrays). Always exactly 4 entries;
 *     // each carries a `heading` string + an `items` array of NavItem.
 *     // (STORY-13.4.04 added the `heading` field for operator editing.)
 *     footer: [
 *       { heading: string, items: NavItem[] },
 *       { heading: string, items: NavItem[] },
 *       { heading: string, items: NavItem[] },
 *       { heading: string, items: NavItem[] },
 *     ],
 *     announcement: {
 *       enabled, text, link?, link_label?,
 *     },
 *   }
 *
 * NavItem:
 *   {
 *     label:        string,
 *     type:         'page' | 'section' | 'url' | 'action',
 *     target:       string,         // page slug | section anchor | URL | action key
 *     open_new_tab: boolean,
 *     icon?:        string,         // optional icon enum (rendered by storefront)
 *   }
 *
 * Trust boundary: `mergeNav(stored)` drops unknown keys, clamps the
 * footer to exactly 4 columns, runs every item through `mergeNavItem`.
 * Storefront reader (`siteSettingsNavService.getNav`) and admin writer
 * (`siteSettingsNavAdmin.updateNav`) both flow through this layer.
 *
 * No Firebase imports — keeps the unit-test surface narrow and lets
 * the seed script reuse the defaults without dragging in the SDK.
 */
import { clampStr, clampEnum, clampUrl, pickKeys } from './sectionContent/_shared';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Allowed `type` values on every NavItem. */
export const NAV_TYPES = Object.freeze(['page', 'section', 'url', 'action']);

/** Allowed icon keys — extensible at code level only. */
export const NAV_ICONS = Object.freeze([
  'home', 'shop', 'collection', 'cart', 'search', 'account',
  'about', 'contact', 'press', 'newsletter',
  'instagram', 'tiktok', 'twitter', 'facebook', 'youtube',
  'email', 'phone', 'pin', 'clock',
]);

/** Footer is always exactly 4 columns. Below this the footer renders blank. */
export const FOOTER_COLUMN_COUNT = 4;

const LABEL_MAX = 80;
const TARGET_MAX = 1024;
const ANNOUNCEMENT_TEXT_MAX = 240;
const ANNOUNCEMENT_LINK_LABEL_MAX = 40;
const HEADER_MAX_ITEMS = 12;
const FOOTER_MAX_ITEMS_PER_COL = 8;     // story-mandated cap (STORY-13.4.04 AC-4)
const FOOTER_HEADING_MAX = 40;

// ---------------------------------------------------------------------------
// Defaults — mirror the existing storefront Header + Footer JSX so a
// brand-new env renders a sensible nav before the operator edits anything.
// ---------------------------------------------------------------------------

const DEFAULT_HEADER = Object.freeze([
  Object.freeze({ label: 'Home',     type: 'page',   target: 'home',     open_new_tab: false }),
  Object.freeze({ label: 'Products', type: 'page',   target: 'products', open_new_tab: false }),
  Object.freeze({ label: 'About',    type: 'page',   target: 'about',    open_new_tab: false }),
  Object.freeze({ label: 'Contact',  type: 'page',   target: 'contact',  open_new_tab: false }),
]);

// Wrapped in `{ items: [...] }` objects because Firestore doesn't
// support nested arrays. Each column is one object; the column's
// items live under the `items` key.
const DEFAULT_FOOTER = Object.freeze([
  // Column 0 — Shop
  Object.freeze({ heading: 'Shop', items: Object.freeze([
    Object.freeze({ label: 'Collections',     type: 'url', target: '/products', open_new_tab: false }),
    Object.freeze({ label: 'Gift Boxes',      type: 'url', target: '/products', open_new_tab: false }),
    Object.freeze({ label: 'Custom Orders',   type: 'url', target: '/contact',  open_new_tab: false }),
    Object.freeze({ label: 'Corporate Gifts', type: 'url', target: '/contact',  open_new_tab: false }),
  ]) }),
  // Column 1 — About
  Object.freeze({ heading: 'About', items: Object.freeze([
    Object.freeze({ label: 'Our Story', type: 'page', target: 'about',   open_new_tab: false }),
    Object.freeze({ label: 'Artisans',  type: 'page', target: 'about',   open_new_tab: false }),
    Object.freeze({ label: 'Contact',   type: 'page', target: 'contact', open_new_tab: false }),
    Object.freeze({ label: 'Press',     type: 'page', target: 'about',   open_new_tab: false }),
  ]) }),
  // Column 2 — Help
  Object.freeze({ heading: 'Help', items: Object.freeze([
    Object.freeze({ label: 'FAQ',                  type: 'page', target: 'faq',      open_new_tab: false }),
    Object.freeze({ label: 'Shipping & Returns',   type: 'page', target: 'shipping', open_new_tab: false }),
    Object.freeze({ label: 'Privacy Policy',       type: 'page', target: 'privacy',  open_new_tab: false }),
    Object.freeze({ label: 'Terms of Service',     type: 'page', target: 'terms',    open_new_tab: false }),
  ]) }),
  // Column 3 — Connect
  Object.freeze({ heading: 'Connect', items: Object.freeze([
    Object.freeze({ label: 'Instagram',  type: 'url',    target: 'https://instagram.com/curatedlagos', open_new_tab: true,  icon: 'instagram' }),
    Object.freeze({ label: 'TikTok',     type: 'url',    target: 'https://tiktok.com/@curatedlagos',  open_new_tab: true,  icon: 'tiktok' }),
    Object.freeze({ label: 'Email',      type: 'url',    target: 'mailto:hello@curatedlagos.com',     open_new_tab: false, icon: 'email' }),
    Object.freeze({ label: 'Newsletter', type: 'action', target: 'newsletter',                        open_new_tab: false, icon: 'newsletter' }),
  ]) }),
]);

const DEFAULT_ANNOUNCEMENT = Object.freeze({
  enabled: false,
  text: '',
  link: '',
  link_label: '',
});

export const DEFAULT_NAV = Object.freeze({
  header: DEFAULT_HEADER,
  footer: DEFAULT_FOOTER,
  announcement: DEFAULT_ANNOUNCEMENT,
});

// ---------------------------------------------------------------------------
// Per-item coercion
// ---------------------------------------------------------------------------

/**
 * Coerce a single stored item to a safe NavItem. Returns `null` for
 * anything that can't be salvaged — callers filter null out (so a
 * malformed entry in Firestore can't break the storefront render).
 */
export function mergeNavItem(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const label = clampStr(raw.label, '', LABEL_MAX);
  if (label.length === 0) return null;        // empty-label rows are dropped
  const type = clampEnum(raw.type, NAV_TYPES, 'url');
  // `target` semantic depends on type:
  //   - url    → arbitrary URL / relative path (clampUrl, 1024)
  //   - page   → page slug (URL-shaped clampStr is fine)
  //   - section→ section id (alpha-numeric-dash)
  //   - action → action key from NAV_ACTIONS
  // We treat all four as length-capped strings here; the storefront
  // resolver does the type-specific lookup at render time.
  const target = type === 'url'
    ? clampUrl(raw.target, '')
    : clampStr(raw.target, '', TARGET_MAX);
  const open_new_tab = raw.open_new_tab === true;
  const item = { label, type, target, open_new_tab };
  if (typeof raw.icon === 'string' && raw.icon.length > 0) {
    item.icon = clampEnum(raw.icon, NAV_ICONS, undefined);
    if (item.icon === undefined) delete item.icon;     // unknown icon → drop
  }
  return item;
}

/**
 * Coerce an array of stored items to a safe array, capped at `max`.
 * Falls back to `defaultItems` (a copy) when the input isn't an array.
 */
function mergeNavItemList(raw, { max, defaultItems }) {
  if (!Array.isArray(raw)) {
    return defaultItems.map((it) => ({ ...it }));
  }
  const out = [];
  for (let i = 0; i < raw.length && out.length < max; i++) {
    const item = mergeNavItem(raw[i]);
    if (item != null) out.push(item);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Top-level merger
// ---------------------------------------------------------------------------

function coerceAnnouncement(raw) {
  const src = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
  return pickKeys(src, {
    enabled:    (v) => v === true,
    text:       (v) => clampStr(v, '', ANNOUNCEMENT_TEXT_MAX),
    link:       (v) => clampUrl(v, ''),
    link_label: (v) => clampStr(v, '', ANNOUNCEMENT_LINK_LABEL_MAX),
  });
}

function coerceFooter(raw) {
  // Always render exactly FOOTER_COLUMN_COUNT columns. The stored
  // shape is `[{heading, items:[…]}, …]` (no nested arrays — Firestore
  // restriction). Missing columns fall back to the default for that
  // slot; extra columns truncated. Missing heading falls back to the
  // default heading for that slot (added in STORY-13.4.04).
  const out = [];
  for (let i = 0; i < FOOTER_COLUMN_COUNT; i++) {
    const storedColumn = Array.isArray(raw) && raw[i] && typeof raw[i] === 'object' && !Array.isArray(raw[i])
      ? raw[i]
      : null;
    const storedItems = storedColumn && Array.isArray(storedColumn.items)
      ? storedColumn.items
      : null;
    const defaultColumn = DEFAULT_FOOTER[i] || { heading: '', items: [] };
    out.push({
      heading: clampStr(storedColumn?.heading, defaultColumn.heading || '', FOOTER_HEADING_MAX),
      items: mergeNavItemList(storedItems, {
        max: FOOTER_MAX_ITEMS_PER_COL,
        defaultItems: defaultColumn.items,
      }),
    });
  }
  return out;
}

/**
 * Coerce a stored `/site_settings/nav` doc to a safe shape.
 * Unknown top-level keys dropped, header capped, footer always 4 cols.
 */
export function mergeNav(stored) {
  const src = (stored && typeof stored === 'object' && !Array.isArray(stored)) ? stored : {};
  return pickKeys(src, {
    header:       (v) => mergeNavItemList(v, { max: HEADER_MAX_ITEMS, defaultItems: DEFAULT_HEADER }),
    footer:       (v) => coerceFooter(v),
    announcement: (v) => coerceAnnouncement(v),
  });
}

export const NAV_LIMITS = Object.freeze({
  labelMax: LABEL_MAX,
  targetMax: TARGET_MAX,
  announcementTextMax: ANNOUNCEMENT_TEXT_MAX,
  announcementLinkLabelMax: ANNOUNCEMENT_LINK_LABEL_MAX,
  headerMaxItems: HEADER_MAX_ITEMS,
  footerMaxItemsPerCol: FOOTER_MAX_ITEMS_PER_COL,
  footerHeadingMax: FOOTER_HEADING_MAX,
  footerColumnCount: FOOTER_COLUMN_COUNT,
  types: NAV_TYPES,
  icons: NAV_ICONS,
});
